import _ from 'lodash/fp'
import gql from 'graphql-tag'
import * as effects from 'redux-saga/effects'
import { apolloClient } from '@fe/business/src/apollo'

import * as pinPositionsReducer from '../reducers/pinPositions'

import * as snack from './snackMessage'

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

export function * loadPinPositionsEffect (pinSetupId) {
  try {
    const response = yield effects.call([apolloClient, apolloClient.query], {
      query: gql`
        query PinPositionsBySetup($input: PinPositionInput!) {
          PinPositionsQuery {
            getPinPositions(input: $input) {
              outMeters
              sideMeters
              position {
                latitude
                longitude
              }
              greenPoint {
                latitude
                longitude
              }
              tPoint {
                latitude
                longitude
              }
              pinSetupId
              holeId
              name
              holesOrder
              lastChanged
            }
          }
        }
      `,
      variables: { input: { pinSetupId } },
      fetchPolicy: 'network-only'
    })

    return _.getOr([], 'data.PinPositionsQuery.getPinPositions', response)
  } catch (error) {
    return { error }
  }
}

const UPSERT_PIN_POSITION_MUTATION = gql`
  mutation UpsertPinPosition($input: UpsertPinPositionInput!) {
    PinPositionsMutation {
      upsertPinPosition(input: $input)
    }
  }
`

function * upsertPinPositionEffect (pinPosition) {
  try {
    const response = yield effects.call([apolloClient, apolloClient.mutate], {
      mutation: UPSERT_PIN_POSITION_MUTATION,
      variables: { input: pinPosition }
    })
    return _.get('data.PinPositionsMutation.upsertPinPosition', response)
  } catch (error) {
    return { error }
  }
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

export function * loadPinPositionsFlow ({ payload }) {
  const result = yield effects.call(loadPinPositionsEffect, payload)
  if (result.error) {
    yield effects.put(pinPositionsReducer.actions.loadError(result.error))
    yield snack.genericError(result.error)
  } else {
    yield effects.put(pinPositionsReducer.actions.loadSuccess(result))
  }
}

const unkeyBy = key => _.flow(
  _.toPairs,
  _.map(([v0, v1]) => _.set(key, v0, v1))
)

const ungroupBy = key => _.flow(
  _.toPairs,
  _.map(([v0, v1]) => _.map(v => _.set(key, v0, v), v1)),
  _.flatten
)

const preparePinPositions = _.flow(
  _.mapValues(unkeyBy('holeId')),
  ungroupBy('pinSetupId'),
  _.map(p => ({
    outMeters: _.parseInt(10, p.in),
    sideMeters: _.parseInt(10, p.side),
    position: {
      longitude: _.get('longitude', p.position),
      latitude: _.get('latitude', p.position)
    },
    pinSetupId: parseInt(p.pinSetupId),
    holeId: parseInt(p.holeId)
  }))
)

function * upsertPinPositionsFlow ({ payload: pinChanges }) {
  const pinPositions = preparePinPositions(pinChanges)
  const results = yield effects.all(_.map(pp => effects.call(upsertPinPositionEffect, pp), pinPositions))
  if (_.isEmpty(results)) {
    yield effects.put(pinPositionsReducer.actions.upsertError(new Error('Adding pin positions went wrong')))
    yield snack.genericError('Adding pin positions went wrong')
  } else {
    yield effects.put(pinPositionsReducer.actions.upsertSuccess(pinPositions))
  }
}

export default function * () {
  return yield effects.all([
    effects.takeEvery(pinPositionsReducer.types.LOAD_REQUEST, loadPinPositionsFlow),
    effects.takeLatest(pinPositionsReducer.types.UPSERT_REQUEST, upsertPinPositionsFlow)
  ])
}
